import { Box, Button, Checkbox, Chip, Group, Popover, Select, Stack, Text, TextInput } from "@mantine/core"
import { BroomIcon, ColumnsIcon } from "@phosphor-icons/react"
import { useSelector } from "react-redux"
import useSwr from "swr"
import { useTranslation } from "react-i18next"
import { OrderApi } from "../../../apis"
import { usePermission } from "../../../components/permission"
import { IGeneral } from "../../../store/General"
import { message } from "../../../utils/message"
import HttpHandler from "../../../utils/http-handler"
import classes from "../list.module.css"
import { SALES_COLUMNS } from "./columns"
import { ConsignmentFilter, Filters } from "./types"

type Props = {
  filters: Filters
  setField: <K extends keyof Filters>(key: K, value: Filters[K]) => void
  consignmentFilter: ConsignmentFilter
  setConsignmentFilter: (v: ConsignmentFilter) => void
  onClear: () => void
  mode?: "sales" | "split"
  visibleColumns: string[]
  setVisibleColumns: (ids: string[]) => void
}

/** Ord_SalesList filter row: consignment chips, business, payment term, status, clear, columns. */
export function SupplierSalesFilterBar({
  filters, setField, consignmentFilter, setConsignmentFilter, onClear, mode = "sales", visibleColumns, setVisibleColumns,
}: Props) {
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_LIST = usePermission("ORD_LIST")
  const ORD_EDIT = usePermission("ORD_EDIT")
  const { paymentTermConditions, orderStatus } = useSelector((s: { general: IGeneral }) => s.general)

  const { data: businessData } = useSwr("/ord/order/business_select/data/", async () => {
    if (ORD_LIST.hasAccess || ORD_EDIT.hasAccess) {
      try {
        const res = await OrderApi.business({ query: "", limit: "" })
        return Array.from(new Map(res.map((item: { value: string }) => [item.value, item])).values())
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
      }
    }
  })

  const statusOptions = (orderStatus || [])
    .filter((o: { code: string }) => !["DRAFT", "REGISTERED", "REVIEW_REJECTED"].includes(o.code))
    .map((o: { code: string; sentName?: string }) => ({ label: o.sentName ?? o.code, value: o.code }))

  return (
    <Group justify="space-between" align="flex-end" gap="md" wrap="wrap" className={classes.filterBar}>
      {mode === "sales" ? <Chip.Group multiple={false} value={consignmentFilter} onChange={(v) => setConsignmentFilter((v as ConsignmentFilter) || "ALL")}>
        <Group gap="xs">
          <Chip value="ALL">{t("supplierSales:sales.consignmentFilters.all")}</Chip>
          <Chip value="REGULAR">{t("supplierSales:sales.consignmentFilters.regular")}</Chip>
          <Chip value="CONSIGNMENT">{t("supplierSales:sales.consignmentFilters.consignment")}</Chip>
          <Chip value="VMI_CRON">{t("supplierSales:sales.consignmentFilters.vmiAuto")}</Chip>
        </Group>
      </Chip.Group> : <Box />}

      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          label={t("supplierSales:sales.list.searchCode")}
          placeholder={t("supplierSales:sales.list.searchCodePlaceholder")}
          value={filters.salesCode ?? ""}
          onChange={(e) => setField("salesCode", e.currentTarget.value)}
          w={180}
        />
        {ORD_LIST.hasAccess && (
          <Select
            label={t("supplierSales:sales.businessName")}
            placeholder={t("common:select")}
            data={((businessData || []) as { profileName: string; id: string }[]).map((item) => ({ label: item.profileName, value: item.id }))}
            value={filters.businessId}
            onChange={(v) => setField("businessId", v)}
            searchable
            clearable
            w={220}
          />
        )}
        {mode === "sales" && <Select
          label={t("supplierSales:sales.paymentTerm")}
          placeholder={t("common:select")}
          data={(paymentTermConditions || []).map((item: { name: string; code: string }) => ({ label: item.name, value: item.code }))}
          value={filters.paymentTermCondition}
          onChange={(v) => setField("paymentTermCondition", v)}
          clearable
          w={200}
        />}
        <Select
          label={t("supplierSales:sales.list.statusFilter")}
          placeholder={t("common:select")}
          data={statusOptions}
          value={filters.orderStatus}
          onChange={(v) => setField("orderStatus", v)}
          clearable
          w={200}
        />
        <Button variant="default" leftSection={<BroomIcon size={16} />} onClick={onClear}>
          {t("supplierSales:sales.list.clear")}
        </Button>
        <Popover position="bottom-end" withArrow shadow="md" width={260}>
          <Popover.Target>
            <Button variant="default" leftSection={<ColumnsIcon size={16} />}>
              {t("supplierSales:sales.list.columnsMenu")}
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs">
              <Text fw={650} fz={14}>{t("supplierSales:sales.list.columnsMenuTitle")}</Text>
              <Box>
                <Checkbox.Group value={visibleColumns} onChange={setVisibleColumns}>
                  <Stack gap="xs">
                    {SALES_COLUMNS.filter((c) => c.optional).map((c) => (
                      <Checkbox key={c.id} value={c.id} label={t(`supplierSales:${c.label}`)} />
                    ))}
                  </Stack>
                </Checkbox.Group>
              </Box>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>
    </Group>
  )
}
